import { useEffect, useState } from 'react';
import type { FC, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Roles } from 'src/enums/role-based-auth-guard';
import { dealerApi } from 'src/api/dealer/dealer';
import { ResponseCodes } from 'src/enums/responseCodes';
import { useAuth } from '../hooks/use-auth';
import { Login } from '../containers/login';
import { AccessDenied } from '../containers/access-denied';
import { CircularLoader } from './common/circularLoader';

interface RoleBasedGuardProps {
  children: ReactNode;
  roles: string[];
}

export const RoleNActiveStatBasedGuard: FC<RoleBasedGuardProps> = (props) => {
  enum ActiveStatus {
    PENDING = 'PENDING',
    ACTIVE = 'ACTIVE',
    INACTIVE = 'INACTIVE',
  }
  const { children, roles } = props;
  const auth = useAuth();
  const location = useLocation();
  const [requestedLocation, setRequestedLocation] = useState<string | null>(null);
  const [activeStatus, setActiveStatus] = useState<ActiveStatus>(ActiveStatus.PENDING);

  // check if user is authenticated or not
  if (!auth.isAuthenticated) {
    if (location.pathname !== requestedLocation) {
      setRequestedLocation(location.pathname);
    }

    return (
      <Login />
    );
  }

  const getDealerActiveStatus = async () => {
    const result = await dealerApi.getDealerProfile();
    if (result?.statusCode === ResponseCodes.OK) {
      setActiveStatus(result?.responseData?.status === 'Active' ? ActiveStatus.ACTIVE : ActiveStatus.INACTIVE);
    }
  };

  useEffect(() => {
    if (auth.user.scope === Roles.DEALER) {
      getDealerActiveStatus();
    }
  }, [auth]);

  // if user is authenticated & is allowed to access the route
  if (auth.isAuthenticated && (roles.includes(auth.user.scope))) {
    if (auth.user.scope === Roles.DEALER) {
      switch (activeStatus) {
        case ActiveStatus.ACTIVE:
          return (
            <>
              {children}
            </>
          );
        case ActiveStatus.INACTIVE:
          return (
            <AccessDenied />
          );
        case ActiveStatus.PENDING:
          return (
            <CircularLoader />
          );
        default:
          return (
            <AccessDenied />
          );
      }
    } else {
      return (
        <>
          {children}
        </>
      );
    }
  }

  // This is done so that in case the route changes by any chance through other
  // means between the moment of request and the render we navigate to the initially
  // requested route.
  if (requestedLocation && location.pathname !== requestedLocation) {
    setRequestedLocation(null);
    return <Navigate to={requestedLocation} />;
  }

  return (
    <AccessDenied />
  );
};

RoleNActiveStatBasedGuard.propTypes = {
  children: PropTypes.node.isRequired,
  roles: PropTypes.arrayOf(PropTypes.string).isRequired,
};
